import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Form } from 'react-bootstrap';
import PlacesService, {
  PlaceAutocompleteResult,
  PlaceDetails,
} from '../api/services/PlacesService';
import { useAppSelector } from '../store';
import { user } from '../features/account/api/accountSlice';

interface PlacesAutocompleteProps {
  onPlaceSelected?: (place: PlaceDetails) => void;
  placeholder?: string;
  className?: string;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  autocompleteRef?: React.RefObject<HTMLInputElement>;
}

const PlacesAutocomplete: React.FC<PlacesAutocompleteProps> = ({
  onPlaceSelected,
  placeholder = 'Enter address',
  className = '',
  value,
  onChange,
  onBlur,
  disabled = false,
  autocompleteRef,
}) => {
  const [suggestions, setSuggestions] = useState<PlaceAutocompleteResult[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const patient = useAppSelector(user);
  const patientUUID = patient?.uuid;

  // Use provided ref or internal ref
  const actualInputRef = autocompleteRef || inputRef;

  useEffect(() => {
    if (value !== undefined) {
      setInputValue(value);
    }
  }, [value]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        actualInputRef.current &&
        !actualInputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [actualInputRef]);

  const searchPlacesDebounced = useCallback(
    async (input: string) => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      if (!input || input.trim().length < 2) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      debounceTimer.current = setTimeout(async () => {
        try {
          setLoading(true);
          const results = await PlacesService.autocomplete({
            input,
            language: 'en',
            components: 'country:us|country:ca|country:mx',
            patient_uuid: patientUUID,
          });
          setSuggestions(results);
          setShowSuggestions(true);
        } catch (error) {
          console.error('Error searching places:', error);
          setSuggestions([]);
        } finally {
          setLoading(false);
        }
      }, 300); // 300ms debounce
    },
    [patientUUID],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange?.(newValue);

    if (!disabled) {
      searchPlacesDebounced(newValue);
    }
  };

  const handleSelectPlace = async (place: PlaceAutocompleteResult) => {
    try {
      setLoading(true);
      setShowSuggestions(false);
      setInputValue(place.description);

      // Fetch place details
      const details = await PlacesService.getPlaceDetails(place.place_id, patientUUID);
      if (details && onPlaceSelected) {
        onPlaceSelected(details);
        // Don't call onChange when selecting from autocomplete - onPlaceSelected handles field population
        // Only update the input value, but don't trigger onChange which might clear other fields
        return;
      }

      // Only call onChange if onPlaceSelected wasn't called (fallback)
      onChange?.(place.description);
    } catch (error) {
      console.error('Error getting place details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputFocus = () => {
    if (inputValue && inputValue.length >= 2) {
      searchPlacesDebounced(inputValue);
    }
  };

  const handleInputBlur = () => {
    // Delay hiding suggestions to allow for selection
    setTimeout(() => {
      setShowSuggestions(false);
      onBlur?.();
    }, 200);
  };

  return (
    <div className={`places-autocomplete-wrapper ${className}`} style={{ position: 'relative' }}>
      <Form.Control
        ref={actualInputRef}
        type='text'
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete='off'
        style={{
          paddingRight: loading ? '40px' : '12px',
        }}
      />
      {loading && (
        <div
          style={{
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
          }}
        >
          <div className='spinner-border spinner-border-sm' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
        </div>
      )}

      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className='list-group'
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            maxHeight: '300px',
            overflowY: 'auto',
            marginTop: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            borderRadius: '4px',
            backgroundColor: 'white',
          }}
        >
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.place_id}
              type='button'
              className='list-group-item list-group-item-action'
              onClick={() => handleSelectPlace(suggestion)}
              style={{
                textAlign: 'left',
                border: 'none',
                borderBottom: '1px solid #e9ecef',
                padding: '12px',
                cursor: 'pointer',
              }}
              onMouseDown={(e) => e.preventDefault()} // Prevent blur before click
            >
              <div style={{ fontWeight: 500, color: '#212529' }}>{suggestion.main_text}</div>
              <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                {suggestion.secondary_text}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlacesAutocomplete;
